import type { NumberType } from '../model/form.types';
import {
  DECIMAL_MAX_DIGITS,
  decimalFromStored,
  effectiveDecimalPlaces,
  formatPtBrDecimal,
  sanitizeDecimalTyping,
} from './decimal';

export interface NumberMaskLimits {
  numberType?: NumberType;
  decimalPlaces?: number;
  minValue?: unknown;
  maxValue?: unknown;
}

// scale null means the field has no fixed number of places (no type, or "Decimal" without a valid
// count), so the text is typed freely; a number means the digits fill from the right, cents first.
export interface NumberMaskOptions {
  scale: number | null;
  maxIntegerDigits: number;
  maxNegativeIntegerDigits: number;
  allowNegative: boolean;
}

function integerDigitsOf(canonical: string): number {
  return canonical.replace(/^-/, '').split('.')[0].length;
}

// A limit caps the integer digits only on the side it closes: a positive maximum bounds the positive
// numbers and a negative minimum bounds the negative ones. A minimum of 0 alone caps nothing, or every
// positive value would stop at one digit.
export function maskOptionsFrom(limits: NumberMaskLimits): NumberMaskOptions {
  const scale =
    limits.numberType === 'integer'
      ? 0
      : (effectiveDecimalPlaces(limits.numberType, limits.decimalPlaces) ?? null);
  const min = decimalFromStored(limits.minValue);
  const max = decimalFromStored(limits.maxValue);
  const ceiling = DECIMAL_MAX_DIGITS - (scale ?? 0);
  const allowNegative = min === null || min.startsWith('-');
  const capOf = (limit: string | null): number =>
    limit === null ? ceiling : Math.min(integerDigitsOf(limit), ceiling);
  return {
    scale,
    maxIntegerDigits: max !== null && max.startsWith('-') ? ceiling : capOf(max),
    maxNegativeIntegerDigits: allowNegative ? capOf(min) : 0,
    allowNegative,
  };
}

export function scaleCanonical(canonical: string, scale: number | null): string {
  if (scale === null) return canonical;
  const [integer, fraction = ''] = canonical.split('.');
  const scaled = scale === 0 ? integer : `${integer}.${fraction.padEnd(scale, '0').slice(0, scale)}`;
  return /^-0(\.0+)?$/.test(scaled) ? scaled.slice(1) : scaled;
}

function isNegative(text: string, options: NumberMaskOptions): boolean {
  if (!options.allowNegative) return false;
  return (text.match(/-/g) ?? []).length % 2 === 1;
}

function renderDigits(digits: string, negative: boolean, scale: number): string {
  const padded = digits.padStart(scale + 1, '0');
  const integer = padded.slice(0, padded.length - scale);
  const body = scale === 0 ? integer : `${integer}.${padded.slice(padded.length - scale)}`;
  return (negative ? '-' : '') + formatPtBrDecimal(body);
}

function capFreeText(text: string, options: NumberMaskOptions): string {
  const kept = sanitizeDecimalTyping(text);
  const unsigned = options.allowNegative ? kept : kept.replace(/^-/, '');
  const commaAt = unsigned.indexOf(',');
  const head = commaAt === -1 ? unsigned : unsigned.slice(0, commaAt);
  const tail = commaAt === -1 ? '' : unsigned.slice(commaAt);
  let budget = unsigned.startsWith('-') ? options.maxNegativeIntegerDigits : options.maxIntegerDigits;
  const cappedHead = head.replace(/\d/g, (digit) => (budget-- > 0 ? digit : ''));
  if (cappedHead === head) return head + tail;
  const sign = cappedHead.startsWith('-') ? '-' : '';
  return formatPtBrDecimal(sign + cappedHead.replace(/\D/g, '')) + tail;
}

// The typed text is read as a digit buffer: every '-' toggles the sign, leading zeros are dropped,
// the digits past the capacity are dropped from the end (the same end a blocked key would have
// filled), and the buffer is shown with the fixed number of places.
export function applyNumberMask(text: string, options: NumberMaskOptions): string {
  if (options.scale === null) return capFreeText(text, options);
  const negative = isNegative(text, options);
  const raw = text.replace(/\D/g, '');
  if (raw.length === 0) return negative ? '-' : '';
  const cap = negative ? options.maxNegativeIntegerDigits : options.maxIntegerDigits;
  const digits = raw.replace(/^0+/, '').slice(0, cap + options.scale);
  return renderDigits(digits, negative, options.scale);
}

export function removeLastMaskedDigit(text: string, options: NumberMaskOptions): string {
  if (options.scale === null) return text.slice(0, -1);
  const negative = isNegative(text, options);
  const raw = text.replace(/\D/g, '');
  if (raw.length === 0) return '';
  const digits = raw.replace(/^0+/, '').slice(0, -1);
  if (digits.length === 0) return negative ? '-' : '';
  return renderDigits(digits, negative, options.scale);
}
